import {
  Input,
  Output,
  Inject,
  Directive,
  OnChanges,
  OnDestroy,
  ElementRef,
  TemplateRef,
  EventEmitter,
  HostListener,
  SimpleChanges,
  EmbeddedViewRef,
  ViewContainerRef,
  DOCUMENT
} from '@angular/core';


/**
 * libs
*/
import { Tooltip } from 'bootstrap';
import { clippingParents } from '@popperjs/core/lib/enums';

/**
 * @directive TooltipDirective
 * @description Angular directive that wraps Bootstrap 5 tooltips with full dynamic configuration.
 */
@Directive({
  selector: '[appTooltip]'
})
export class TooltipDirective implements OnChanges, OnDestroy {

  /**
   * @description Bootstrap Tooltip instance for programmatic control.
  */
  private _tooltipInstance?: Tooltip;

  /**
   * @description Optional embedded view if using TemplateRef
  */
  private _viewTitleRef?: EmbeddedViewRef<any>;

  /**
   * @description Optional embedded view if using TemplateRef
  */
  private _viewTemplateRef!: EmbeddedViewRef<any>;

  //--------------------------------------------------------------------------
  // Event Handling (Host Listeners)
  //--------------------------------------------------------------------------

  /**
   * @event
   * Handles when tooltip is about to be show after animation via Bootstrap event.
   * Emits shown as void.
   * @param $event - Bootstrap shown.bs.tooltip event
   */
  @HostListener('shown.bs.tooltip', ['$event'])
  public shownTooltip($event: Event): any {
    this.shown.emit();
  }

  /**
   * @event
   * Handles when tooltip is completely hidden after animation via Bootstrap event.
   * Emits tooltipHidden as void.
   * @param $event - Bootstrap hidden.bs.tooltip event
   */
  @HostListener('hidden.bs.tooltip', ['$event'])
  public hiddenTooltip($event: Event) {
    this.hidden.emit();
  }

  /**
   * @description Event emitted when the tooltip is shown.
   */
  @Output('tooltipShown')
  public shown = new EventEmitter<void>();  

  /**
   * @description Event emitted when the tooltip is hidden.
   */
  @Output('tooltipHidden')
  public hidden = new EventEmitter<void>();

  /**
   * 
   */
  @Input({ alias: 'tooltipDisabled' })
  public set disabled(value: boolean) {
    
    // update tooltip state
    queueMicrotask(() => value ? this._disable() : this._enable());

    // update ref 
    this._disabled = value;

  }
  public get disabled(): boolean {
    return this._disabled;
  }
  private _disabled: boolean = false;

  /**
   * @description Sets the title of the tooltip.
   * Accepts a string, HTMLElement, or Angular TemplateRef.
   * @param value Title content
   */
  @Input({ alias: 'appTooltip' })
  public set title(value: Tooltip.Options['title'] | TemplateRef<any>) {

    // void
    if (value == null) return;

    // check if value is TemplateRef
    if (value instanceof TemplateRef) {

      // destroyed old contentRef
      if (this._viewTitleRef != null) this._viewTitleRef.destroy();

      // ES6 Destruction
      const { element, viewRef } = this.renderTemplateToElement(value);
      
      // save current viewRef to handler it with destroyed
      this._viewTitleRef = viewRef;
      
      value = element;
      
    }
    else if (value instanceof HTMLElement) {
      value = this._document.createDocumentFragment().appendChild(value);
    }

    this._title = value;
    
  }
  public get title(): Tooltip.Options['title'] {
    return this._title
  }
  private _title!: Tooltip.Options['title']

  /**
   * @description Sets a custom template for the entire tooltip structure.
   * Accepts a raw HTML string, HTMLElement, or Angular TemplateRef.
   * @param value Custom template
   * @default 
   *  <div class="tooltip" role="tooltip">
        <div class="tooltip-inner"></div>
        <div class="tooltip-arrow"></div>
      </div>
   */
  @Input({ alias: 'tooltipTemplate' })
  public set template(value: Tooltip.Options['template'] | TemplateRef<any> | HTMLElement) {
    // check if value is TemplateRef
    if (value instanceof TemplateRef) {
      
      // destroyed old contentRef
      if (this._viewTemplateRef != null) this._viewTemplateRef.destroy();

      // ES6 Destruction
      const { element, viewRef } = this.renderTemplateToElement(value);
      
      // save current viewRef to handler it with destroyed
      this._viewTemplateRef = viewRef;
      
      value = element.innerHTML;
      
    }
    else if (value instanceof HTMLElement) {
      value = this._document.createDocumentFragment().appendChild(value).innerHTML;
    }
    
    this._template = value;

  }
  public get template(): Tooltip.Options['template'] {
    return this._template;
  }
  private _template!: Tooltip.Options['template']
  
  /**
   * @description Whether to enable the fade animation on the tooltip.
   * @default true
   */
  @Input({ alias: 'tooltipAnimation' })
  public animation: boolean = true;

  /**
   * @description Defines the boundary element used to constrain tooltip positioning.
   * Common values include 'clippingParents', 'viewport', or a specific HTMLElement.
   * @default clippingParents
   */
  @Input({ alias: 'tooltipBoundary' })
  public boundary: Tooltip.Options['boundary'] = clippingParents;

  /**
   * @description Appends the tooltip to a specific container instead of placing it next to the trigger element.
   * Useful for controlling z-index or avoiding clipping.
   * @default false
   */
  @Input({ alias: 'tooltipContainer' })
  public container: Tooltip.Options['container'] = false;

  /**
   * @description Adds a custom CSS class (or classes) to the tooltip.
   * Can be a string or a function returning a string.
   */
  @Input({ alias: 'tooltipCustomClass' })
  public customClass!: Tooltip.Options['customClass']

  /**
   * @description Delay (in ms) before showing or hiding the tooltip.
   * Can be a single number or an object `{ show: number, hide: number }`.
   * @default 0
   */
  @Input({ alias: 'delay' })
  public delay: Tooltip.Options['delay'] = 0
  
  /**
   * @description Specifies fallback placements if the preferred placement is not available.
   * @default '[top, right, bottom, left]'
   */
  @Input({ alias: 'fallbackPlacements' })
  public fallbackPlacements: Tooltip.Options['fallbackPlacements'] = ['top', 'right', 'bottom', 'left'];

  /**
   * @description Offset of the tooltip relative to its trigger element.
   * Can be a number, string, or function returning one.
   * @default [0, 6]
   */
  @Input({ alias: 'tooltipOffset' })
  public offset: Tooltip.Options['offset'] = [0, 6];

  /**
   * @description Preferred placement of the tooltip relative to the trigger.
   * Can be 'auto', 'top', 'bottom', 'left', 'right',
   * @default 'right'
   */
  @Input({ alias: 'tooltipPlacement' })
  public placement: Tooltip.Options['placement'] = 'top'

  /**
   * @description Custom Popper.js configuration object for fine-tuned control of the tooltip behavior.
   */
  @Input({ alias: 'popperConfig' })
  public popperConfig!: Tooltip.Options['popperConfig'];

  /**
   * @description Whether to sanitize the content to prevent XSS attacks.
   * Highly recommended when using dynamic HTML content.
   * @default true
   */
  @Input({ alias: 'tooltipSanitize' })
  public sanitize: Tooltip.Options['sanitize'] = true;

  /**
   * @description Custom sanitization function to override the default sanitizer.
   */
  @Input({ alias: 'tooltipSanitizeFn' })
  public sanitizeFn!: Tooltip.Options['sanitizeFn'];

  /**
   * @description A CSS selector string that is used to trigger the tooltip on multiple elements.
   * Optional when attaching to a single element.
   * @default false
   */
  @Input({ alias: 'tooltipSelector' })
  public selector: Tooltip.Options['selector'] = false;

  /**
   * @description Specifies how the tooltip is triggered.
   * Valid options are 'click', 'hover', 'focus', or a combination like 'hover focus'.
   * @default 'hover focus'
   */
  @Input({ alias: 'tooltipTrigger' })
  public trigger: Tooltip.Options['trigger'] = "hover focus";

  /**
   * @description Defines a whitelist of HTML elements and attributes that are allowed when sanitizing HTML content.
   * Applies only if sanitization is enabled.
   */
  @Input({ alias: 'tooltipAllowList' })
  public allowList!: Tooltip.Options['allowList'];

  /**
   * @description Determines whether the tooltip should render HTML content.
   * Returns true if title, content, or template is an object (like a TemplateRef).
   */
  public get isHtml(): boolean {

    const isHtmlRef: boolean = this.title instanceof Object || this.template != null;

    return isHtmlRef;

  }

  /**
   * @description Bootstrap Tooltip Options for programmatic control.
  */
  private get _tooltipOptions():  Partial<Tooltip.Options> {
    return {
      html: this.isHtml,
      title: this.title,
      delay: this.delay,
      offset: this.offset,
      trigger: this.trigger,
      template: this.template,
      selector: this.selector,
      sanitize: this.sanitize,
      boundary: this.boundary,
      allowList: this.allowList,
      placement: this.placement,
      animation: this.animation,
      container: this.container,
      sanitizeFn: this.sanitizeFn,
      customClass: this.customClass,
      popperConfig: this.popperConfig,
      fallbackPlacements: this.fallbackPlacements,
    }
  }

  /**
   * @description to filter TooltipOptions from any nullable values
   */
  private get _normalizedTooltipOptions(): Partial<Tooltip.Options> {
    // init
    const _options: Partial<Tooltip.Options> = {...this._tooltipOptions};

    // loop in object to clear it from nullable values
    for (const key in _options) {

      // init 
      const _key = key as keyof typeof _options;
      const element = _options[_key];

      // delete/void iterable
      if (element == null) delete _options[_key];
      else continue;

    }

    // return options without nullable values
    return _options;

  }

  /**
   * 
   * @param _vcr ViewContainerRef angular/core
   * @param _document Document angular/core
   * @param _elementRef ElementRef angular/core
   */
  constructor(
    private _vcr: ViewContainerRef,
    @Inject(DOCUMENT) private _document: Document,
    public elementRef: ElementRef<HTMLElement>,
  ) {}

  /**
   * @param changes 
   */
  ngOnChanges(changes: SimpleChanges): void {

    // detect new inputs value that match with offcanvas options
    const keys: string[] = Object.keys(this._tooltipOptions).filter(key => key in changes && (changes[key].currentValue != changes[key].previousValue));

    // render if input has new values
    if (keys.length > 0 || this._tooltipInstance == null) this.renderTooltip();
    
  }

  /**
   * @description Programmatically renders a new Tooltip instance with current options.
   * Disposes any existing instance before re-initializing.
   */
  public renderTooltip(): void {

    // destroy old element
    if (this._tooltipInstance != null) {
      this.dispose();
    }

    // create instance
    this._tooltipInstance = new Tooltip(this.elementRef.nativeElement, this._normalizedTooltipOptions);

  }

  /** 
   * @description Shows the tooltip manually.
   */
  public show(): void {
    this._tooltipInstance?.show();
  }

  /**
   * @description Hides the tooltip manually.
   */
  public hide(): void {
    this._tooltipInstance?.hide();
  }

  /**
   * @description Toggles the visibility of the tooltip.
   */
  public toggle(): void {
    this._tooltipInstance?.toggle();
  }

  /** 
   * @description Enables the tooltip (if previously disabled).
   */
  private _enable(): void {
    this._tooltipInstance?.enable();
  }

  /** 
   * @description Disables the tooltip (preventing it from opening).
   */
  private _disable(): void {
    this._tooltipInstance?.disable();
  }

  /**
   * @description Toggles the enabled/disabled state of the tooltip.
   */
  public toggleEnabled(): void {
    this._tooltipInstance?.toggleEnabled();
  }  

  /** 
   * @description Manually updates the tooltip’s position.
   */
  public update(): void {
    this._tooltipInstance?.update();
  }

  /**
   * @description Sets the content of the tooltip programmatically.
   * @param content Object with new title/content values
   */
  public setContent(content:  Record<string, string | Element | Tooltip.SetContentFunction | null>): void {
    this._tooltipInstance?.setContent(content);
  }

  /**
   * @description Returns the rendered Bootstrap tooltip tip element.
   * Resolved via the `aria-describedby` attribute that Bootstrap sets on the host when the tooltip is shown.
   * Returns `null` when the tooltip is not currently visible.
   */
  public get tipElement(): HTMLElement | null {
    const id = this.elementRef.nativeElement.getAttribute('aria-describedby');
    return id ? this._document.getElementById(id) : null;
  }

  /**
   * @description Disposes the current Tooltip instance and any rendered views.
   */
  public dispose(): void {
    this._tooltipInstance?.dispose();
  }

  /**
   * Renders an Angular TemplateRef into an HTMLElement using a span wrapper
   * with `display: contents` to avoid layout interference.
   * Also returns the EmbeddedViewRef for lifecycle management.
   * 
   * @param templateRef Angular TemplateRef to render
   * @returns Object containing the rendered element and the EmbeddedViewRef
   */
  private renderTemplateToElement(templateRef: TemplateRef<any>): { element: HTMLElement, viewRef: EmbeddedViewRef<any> } {

    // unset span to be like Fragment
    let container = this._document.createElement('span');
    container.style.all = "unset"
    container.style.display = "contents"

    // create view
    const viewRef = this._vcr.createEmbeddedView(templateRef);

    // append view content in span fragment
    viewRef
      .rootNodes
      .forEach((node: Node) => container.appendChild(node));
    
    return { element: container as unknown as HTMLElement, viewRef }

  }

  /**
   * 
   */
  ngOnDestroy(): void {
    this.dispose();
    this._viewTitleRef?.destroy();
    this._viewTemplateRef?.destroy();
  }

}